import React from 'react';
import { Users, AlertTriangle, TrendingUp, ClipboardList } from 'lucide-react';
import { getEpsColor } from '@/lib/scoring';

interface CoachKpiCardsProps {
  avgEps: number;
  belowThreshold: number;
  mostImproved: string;
  sessionsThisMonth: number;
  totalAthletes: number;
}

export default function CoachKpiCards({ avgEps, belowThreshold, mostImproved, sessionsThisMonth, totalAthletes }: CoachKpiCardsProps) {
  const epsColor = getEpsColor(avgEps);

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
      {/* Squad Avg EPS */}
      <div
        className="rounded-xl p-4 col-span-2 xl:col-span-1"
        style={{
          background: `rgba(20,20,20,0.85)`,
          border: `1px solid rgba(225,6,0,0.2)`,
          boxShadow: `0 0 24px rgba(225,6,0,0.06)`,
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Squad Avg EPS</p>
          <Users size={15} style={{ color: epsColor }} />
        </div>
        <p className="font-mono tabular-nums font-black text-4xl mb-1" style={{ color: epsColor }}>
          {totalAthletes > 0 ? avgEps : '—'}
        </p>
        <p className="text-xs text-zinc-500">
          {totalAthletes > 0 ? `${totalAthletes} athlete${totalAthletes !== 1 ? 's' : ''} assessed` : 'No athletes yet'}
        </p>
      </div>

      {/* Below Threshold */}
      <div
        className="rounded-xl p-4"
        style={{
          background: belowThreshold > 0 ? 'rgba(139,0,0,0.12)' : 'rgba(20,20,20,0.85)',
          border: belowThreshold > 0 ? '1px solid rgba(139,0,0,0.3)' : '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">At Risk</p>
          <AlertTriangle size={15} className={belowThreshold > 0 ? 'text-red-500' : 'text-zinc-600'} />
        </div>
        <p className={`font-mono tabular-nums font-black text-4xl mb-1 ${belowThreshold > 0 ? 'text-red-500' : 'text-zinc-400'}`}>{belowThreshold}</p>
        <p className="text-xs text-zinc-500">Athletes EPS &lt;65</p>
      </div>

      {/* Most Improved */}
      <div
        className="rounded-xl p-4"
        style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Top EPS</p>
          <TrendingUp size={15} className="text-emerald-400" />
        </div>
        <p className="font-semibold text-zinc-200 text-base mb-1 truncate">
          {mostImproved !== '—' ? mostImproved : 'No data yet'}
        </p>
        <p className="text-xs text-zinc-500">Highest EPS in squad</p>
      </div>

      {/* Sessions This Month */}
      <div
        className="rounded-xl p-4"
        style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Sessions</p>
          <ClipboardList size={15} style={{ color: '#E10600' }} />
        </div>
        <p className="font-mono tabular-nums font-black text-4xl text-zinc-200 mb-1">{sessionsThisMonth}</p>
        <p className="text-xs text-zinc-500">Test sessions recorded</p>
      </div>
    </div>
  );
}